import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useT, useLang } from '../i18n';

// Worked example shown at the character-photo upload: one ideal shot next to the
// six framings that ruin the likeness. The card is pre-rendered per locale by
// server/scripts/genPhotoGuide.js — its captions are baked into the image, so
// there is nothing to translate here beyond picking the file.
export default function PhotoGuide() {
  const t = useT();
  const { lang } = useLang();
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (!zoomed) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setZoomed(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [zoomed]);

  // Two shapes of the same card. The inline thumbnail always uses the portrait
  // cut: it lives inside the tips popover, which is capped at 420px on every
  // screen size — the wide card's captions are unreadable at that width.
  // WebP first (a fifth of the PNG's bytes), PNG behind it as the fallback.
  const base = `/samples/photo-guide-${lang === 'he' ? 'he' : 'ar'}`;
  const src = `${base}.png`;
  const tallSrc = `${base}-tall.png`;
  const NARROW = '(max-width: 700px)';

  // Full-screen, a desktop viewport has the room for the wide layout.
  const zoomSources = (
    <>
      <source media={NARROW} type="image/webp" srcSet={`${base}-tall.webp`} />
      <source media={NARROW} srcSet={tallSrc} />
      <source type="image/webp" srcSet={`${base}.webp`} />
    </>
  );

  return (
    <div className="photo-guide">
      <button
        type="button"
        className="photo-guide-card"
        onClick={() => setZoomed(true)}
        aria-label={t.photoGuideZoom}
      >
        <picture>
          <source type="image/webp" srcSet={`${base}-tall.webp`} />
          <img src={tallSrc} alt={t.photoGuideAlt} loading="lazy" />
        </picture>
        <span className="photo-guide-zoom" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="11" y1="8" x2="11" y2="14" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
          {t.photoGuideZoom}
        </span>
      </button>

      {zoomed && createPortal((
        <div
          className="photo-guide-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t.photoGuideAlt}
          onClick={() => setZoomed(false)}
        >
          <button
            type="button"
            className="photo-guide-overlay-close"
            aria-label={t.photoGuideClose}
            onClick={() => setZoomed(false)}
          >
            ×
          </button>
          <picture onClick={(e) => e.stopPropagation()}>
            {zoomSources}
            <img src={src} alt={t.photoGuideAlt} />
          </picture>
        </div>
      ), document.body)}
    </div>
  );
}
